import { useState, useEffect } from "react";
import { ChevronRight, ChevronLeft, X, Sparkles } from "lucide-react";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../../../components/ui/pagination";

// These columns, when present in the agent's response, should always sit
// together and in this order — the rest of the columns follow after them.
const PRIORITY_COLUMNS = [
  "Supplier_Name",
  "Supplied_Name",
  "Cleaned_Supplier_Name",
  "Cleaned_Supplier_Name_Confidence",
  "Immediate_Parent_Name",
  "Immediate_Parent_Name_Confidence",
  "Ultimate_Parent_Name",
  "Ultimate_Parent_Name_Confidence",
];

type SupplierConsolidationStepProps = {
    data?: unknown;
    onNext: (data: any) => void;
    onBack: () => void;
    onCancel: () => void;
};

export function SupplierConsolidationStep({ data, onNext, onBack, onCancel }: SupplierConsolidationStepProps) {
  type TableRow = {
    [key: string]: any;
    id?: string;
  };

  type SidebarItemProps = {
        label: string;
        count: string;
        active?: boolean;
    };
    const SidebarItem = ({
    label,
    count,
    active = false,
    }: SidebarItemProps) => (
    <div className={`sidebar-item ${active ? "active" : ""}`}>

        {/* ✅ LEFT: DOT + LABEL */}
        <div className="sidebar-left">
        <span className="label">{label}</span>
        </div>

        {/* ✅ RIGHT: COUNT */}
        <span className="count">{count}</span>
    </div>
    );

  const [tableData, setTableData] = useState<TableRow[]>([]);
  const [leftCollapsed, setLeftCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);

    const normalizeSupplierRows = (value: unknown): TableRow[] => {
        const isObjectRowArray = (arr: unknown): arr is Record<string, unknown>[] => {
            return Array.isArray(arr) && arr.every((item) => item && typeof item === "object" && !Array.isArray(item));
        };

        if (isObjectRowArray(value)) {
            return value as TableRow[];
        }

        if (value && typeof value === "object") {
            const candidate = value as Record<string, unknown>;

            if (isObjectRowArray(candidate.input_data)) {
                return candidate.input_data as TableRow[];
            }

            if (isObjectRowArray(candidate.data)) {
                return candidate.data as TableRow[];
            }

            if (isObjectRowArray(candidate.result)) {
                return candidate.result as TableRow[];
            }

            for (const nestedValue of Object.values(candidate)) {
                if (isObjectRowArray(nestedValue)) {
                    return nestedValue as TableRow[];
                }
            }
        }

        return [];
    };

  useEffect(() => {
        const simplAiRows = normalizeSupplierRows(data);
        console.log("✅ SIMPLAI rows for consolidation:", simplAiRows);

        if (simplAiRows.length > 0) {
            setTableData(simplAiRows);
            return;
        }

        console.warn("⚠️ SIMPLAI rows missing, no data to display");
        setTableData([]);
    }, [data]);

  const columns = (() => {
    if (tableData.length === 0) return [];

    const allColumns = Object.keys(tableData[0]).filter((key) => key !== "id");
    const presentColumns = new Set(allColumns);

    const orderedPriorityColumns = PRIORITY_COLUMNS.filter((column) => presentColumns.has(column));
    const remainingColumns = allColumns.filter((column) => !PRIORITY_COLUMNS.includes(column));

    return [...orderedPriorityColumns, ...remainingColumns];
  })();

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    setPage(1);
  }, [tableData.length, rowsPerPage]);

  const totalPages = Math.max(1, Math.ceil(tableData.length / rowsPerPage));
  const safePage = Math.min(page, totalPages);
  const pageStartIndex = (safePage - 1) * rowsPerPage;
  const pageRows = tableData.slice(pageStartIndex, pageStartIndex + rowsPerPage);

  /* ================= ✅ HANDLE NEXT ================= */
  // Spend Classification now loads directly from
  // spend_data_enrichment_results, so this step no longer needs to run
  // the SimplAI agent (tool_id 11) or poll for its result before moving on.
  const handleNext = () => {
    onNext(tableData);
  };

  return (
    <div className="setup-screen pad-0 bg-transparent">
      <div className={`qc-layout${leftCollapsed ? " qc-layout--left-collapsed" : ""}${rightCollapsed ? " qc-layout--right-collapsed" : ""}`}>
            {/* ✅ LEFT SIDEBAR */}
            <div className={`qc-sidebar${leftCollapsed ? " is-collapsed" : ""}`}>
                <div className="border-bottom pad-12 qc-sidebar-head">
                    {!leftCollapsed && (
                      <div>
                        <div className="sidebar-title">Post  Processing</div>
                        <div className="sidebar-sub">Before human review</div>
                      </div>
                    )}
                    <button
                      type="button"
                      className="qc-sidebar-toggle"
                      onClick={() => setLeftCollapsed((value) => !value)}
                      aria-label={leftCollapsed ? "Expand post-processing summary" : "Collapse post-processing summary"}
                      title={leftCollapsed ? "Expand" : "Collapse"}
                    >
                      {leftCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
                    </button>
                </div>
                {!leftCollapsed && (
                  <div className="sidebar-list pad-12">
                      <SidebarItem label="Total Unique Suppliers" count="12,481" />
                      <SidebarItem label="Total Normalized Suppliers" count="11,842" />
                      <SidebarItem label="Total Parent Mappings" count="3,218" />
                      <SidebarItem label="Total Spend Value" count="$2.84B" />
                      <SidebarItem label="Human Review Required" count="412" />
                  </div>
                )}
            </div>
            <div className="qc-main">
                <div className="qc-filters">
                    <div className="filter-title">FILTERS</div>
                    <div className="filter-tabs">
                        <span className="tab active">Confidence &lt; 80%</span>
                        <span className="tab">Spend &gt; $50K</span>
                        <span className="tab">Country: All</span>
                    </div>
                    <div className="filter-sort">
                        Sort:
                        <select>
                        <option>Confidence</option>
                        </select>
                    </div>
                </div>
                <div className="qc-table-card">
                    <div className="border-bottom pad-12">
                        <div className="sidebar-title">Human review queue</div>
                        <div className="sidebar-sub">Before human review</div>
                    </div>
                    <div className="table-actions pad-12 border-bottom">
                        <button className="table-action-btn btn-success">
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M6 0C2.68352 0 0 2.6838 0 6C0 9.31648 2.6838 12 6 12C9.31648 12 12 9.3162 12 6C12 2.68352 9.3162 0 6 0ZM6 11.2C3.13273 11.2 0.799992 8.8673 0.799992 6C0.799992 3.1327 3.13273 0.799992 6 0.799992C8.86727 0.799992 11.2 3.13273 11.2 6C11.2 8.86727 8.86727 11.2 6 11.2Z" fill="#00A63E"/>
                                <path d="M9.17497 3.79685C9.01876 3.64063 8.76547 3.64063 8.60928 3.79685L5.04826 7.35787L3.38841 5.69805C3.2322 5.54184 2.97891 5.54184 2.82272 5.69805C2.66651 5.85426 2.66651 6.10753 2.82272 6.26374L4.76539 8.2064C4.92158 8.36259 5.17494 8.36254 5.33108 8.2064L9.17497 4.36251C9.33118 4.2063 9.33118 3.95306 9.17497 3.79685Z" fill="#00A63E"/>
                            </svg>
                            Approve Parent
                        </button>
                        <button className="table-action-btn btn-danger">
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M10.2426 1.75736C9.11742 0.632141 7.5913 0 6 0C4.4087 0 2.88258 0.632141 1.75736 1.75736C0.632141 2.88258 0 4.4087 0 6C0 7.5913 0.632141 9.11742 1.75736 10.2426C2.88258 11.3679 4.4087 12 6 12C7.5913 12 9.11742 11.3679 10.2426 10.2426C11.3679 9.11742 12 7.5913 12 6C12 4.4087 11.3679 2.88258 10.2426 1.75736ZM9.63839 9.63839C8.91847 10.3558 8.00212 10.8438 7.00504 11.0408C6.00797 11.2378 4.97488 11.1349 4.03623 10.7451C3.09759 10.3554 2.29548 9.69624 1.73119 8.85093C1.1669 8.00562 0.865746 7.01206 0.865746 5.99571C0.865746 4.97937 1.1669 3.98581 1.73119 3.1405C2.29548 2.29519 3.09759 1.63604 4.03623 1.24629C4.97488 0.856532 6.00797 0.753649 7.00504 0.950631C8.00212 1.14761 8.91847 1.63562 9.63839 2.35304C10.1178 2.83081 10.4982 3.39851 10.7578 4.02361C11.0173 4.6487 11.1509 5.31888 11.1509 5.99571C11.1509 6.67255 11.0173 7.34273 10.7578 7.96782C10.4982 8.59291 10.1178 9.16062 9.63839 9.63839ZM7.81705 4.7872L6.60425 6L7.81705 7.2128C7.89687 7.29309 7.94167 7.40171 7.94167 7.51492C7.94167 7.62814 7.89687 7.73676 7.81705 7.81705C7.73676 7.89687 7.62814 7.94167 7.51492 7.94167C7.40171 7.94167 7.29309 7.89687 7.2128 7.81705L6 6.60425L4.7872 7.81705C4.70691 7.89687 4.59829 7.94167 4.48508 7.94167C4.37186 7.94167 4.26324 7.89687 4.18295 7.81705C4.10313 7.73676 4.05833 7.62814 4.05833 7.51492C4.05833 7.40171 4.10313 7.29309 4.18295 7.2128L5.39575 6L4.18295 4.7872C4.11274 4.70522 4.07606 4.59977 4.08022 4.49191C4.08439 4.38406 4.1291 4.28174 4.20542 4.20542C4.28174 4.1291 4.38406 4.08439 4.49191 4.08022C4.59977 4.07606 4.70522 4.11274 4.7872 4.18295L6 5.39575L7.2128 4.18295C7.29478 4.11274 7.40023 4.07606 7.50809 4.08022C7.61594 4.08439 7.71826 4.1291 7.79458 4.20542C7.8709 4.28174 7.91561 4.38406 7.91978 4.49191C7.92394 4.59977 7.88726 4.70522 7.81705 4.7872Z" fill="#E0044E"/>
                            </svg>
                            Reject
                        </button>
                        <button className="table-action-btn btn-outline">
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1.1766 8.67556C1.05838 8.67556 0.940384 8.63054 0.850099 8.54025C0.669759 8.35991 0.669759 8.06758 0.850099 7.88724L8.06693 0.670411C8.24704 0.490071 8.5396 0.490071 8.71994 0.670411C8.90028 0.850751 8.90028 1.14308 8.71994 1.32342L1.50311 8.54025C1.41305 8.63031 1.29483 8.67556 1.1766 8.67556Z" fill="black"/>
                                <path d="M0.46228 11.9995C0.430183 11.9995 0.397625 11.996 0.365067 11.9891C0.115685 11.9356 -0.0431803 11.6901 0.0103906 11.4407L0.723899 8.11493C0.77747 7.86555 1.02408 7.70714 1.27231 7.76025C1.52169 7.81382 1.68056 8.05928 1.62698 8.30866L0.913476 11.6344C0.867063 11.8513 0.675409 11.9995 0.46228 11.9995Z" fill="black"/>
                                <path d="M3.78793 11.2847C3.66971 11.2847 3.55171 11.2397 3.46143 11.1494C3.28109 10.9691 3.28109 10.6767 3.46143 10.4964L10.6783 3.27979C10.8584 3.09945 11.1509 3.09945 11.3313 3.27979C11.5116 3.46013 11.5116 3.75246 11.3313 3.9328L4.11467 11.1494C4.02438 11.2397 3.90616 11.2847 3.78793 11.2847Z" fill="black"/>
                                <path d="M0.461518 12.0004C0.248389 12.0004 0.0569657 11.8522 0.0103221 11.6354C-0.0430179 11.386 0.115617 11.1405 0.364998 11.087L3.69078 10.3735C3.94039 10.3206 4.18585 10.479 4.23919 10.7281C4.29253 10.9775 4.1339 11.223 3.88451 11.2765L0.558731 11.9901C0.526173 11.9972 0.493615 12.0004 0.461518 12.0004Z" fill="black"/>
                                <path d="M9.69708 5.3757C9.57886 5.3757 9.46063 5.33067 9.37058 5.24038L6.7583 2.62811C6.57796 2.44777 6.57796 2.15544 6.7583 1.9751C6.93841 1.79476 7.2312 1.79476 7.41131 1.9751L10.0236 4.58737C10.2039 4.76771 10.2039 5.06004 10.0236 5.24038C9.93353 5.33067 9.81531 5.3757 9.69708 5.3757Z" fill="black"/>
                                <path d="M11.0052 4.07C10.8869 4.07 10.7687 4.02497 10.6784 3.93469C10.4981 3.75435 10.4981 3.46202 10.6784 3.28145C10.9354 3.02445 11.077 2.67647 11.077 2.30193C11.077 1.9274 10.9354 1.57942 10.6784 1.32242C10.4212 1.06518 10.0732 0.923636 9.69868 0.923636C9.32414 0.923636 8.97616 1.06518 8.71916 1.32242C8.53905 1.50276 8.24672 1.50299 8.06592 1.32242C7.88558 1.14208 7.88558 0.849745 8.06592 0.669174C8.49726 0.237605 9.07707 0 9.69868 0C10.3201 0 10.9001 0.237605 11.3314 0.669174C11.763 1.10051 12.0006 1.68032 12.0006 2.30193C12.0006 2.92354 11.763 3.50335 11.3314 3.93469C11.2416 4.02474 11.1234 4.07 11.0052 4.07Z" fill="black"/>
                            </svg>
                            Edit grid
                        </button>
                        <button className="table-action-btn btn-outline">
                            <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M11.4 4.2H7.56C6.9 4.2 6.24 4.02 5.64 3.72C5.04 3.42 4.56 2.94 4.14 2.4C3.6 1.68 2.7 1.2 1.74 1.2H0.6C0.24 1.2 0 0.96 0 0.6C0 0.24 0.24 0 0.6 0H1.74C3.06 0 4.32 0.66 5.1 1.74C5.34 2.1 5.7 2.46 6.12 2.64C6.54 2.88 7.02 2.94 7.44 2.94H11.4C11.76 2.94 12 3.18 12 3.54C12 3.9 11.76 4.2 11.4 4.2Z" fill="black"/>
                                <path d="M1.8 7.2H0.6C0.24 7.2 0 6.96 0 6.6C0 6.24 0.24 6 0.6 6H1.8C2.28 6 2.76 5.88 3.12 5.7C3.54 5.46 3.9 5.16 4.14 4.8C4.5 4.26 5.04 3.84 5.64 3.48C6.24 3.18 6.9 3 7.56 3H10.8C11.16 3 11.4 3.24 11.4 3.6C11.4 3.96 11.16 4.2 10.8 4.2H7.5C7.02 4.2 6.54 4.32 6.18 4.5C5.76 4.74 5.4 5.04 5.16 5.4C4.8 5.94 4.26 6.36 3.66 6.72C3.06 7.02 2.4 7.2 1.8 7.2Z" fill="black"/>
                                <path d="M9.6 5.99922C9.42 5.99922 9.3 5.93922 9.18 5.81922C8.94 5.57922 8.94 5.21922 9.18 4.97922L10.56 3.59922L9.18 2.21922C8.94 1.97922 8.94 1.61922 9.18 1.37922C9.42 1.13922 9.78 1.13922 10.02 1.37922L11.82 3.17922C12.06 3.41922 12.06 3.77922 11.82 4.01922L10.02 5.81922C9.9 5.93922 9.78 5.99922 9.6 5.99922Z" fill="black"/>
                            </svg>
                            Merge Suppliers
                        </button>
                        <button className="table-action-btn btn-outline">
                            <svg width="12" height="13" viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M4.90337 1.25326e-05H7.0967C7.23706 -7.82777e-05 7.35933 -0.000156045 7.47479 0.0182849C7.93098 0.0911343 8.32575 0.375662 8.53915 0.785405C8.59312 0.889117 8.63172 1.00515 8.67602 1.13833L8.74847 1.35556C8.76073 1.39234 8.76423 1.40275 8.76715 1.41095C8.8808 1.72497 9.17528 1.93724 9.50907 1.9457C9.5179 1.94592 9.5286 1.94596 9.56758 1.94596H11.5135C11.7822 1.94596 12 2.16377 12 2.43244C12 2.70112 11.7822 2.91893 11.5135 2.91893H0.486484C0.217809 2.91893 0 2.70112 0 2.43244C0 2.16377 0.217809 1.94596 0.486484 1.94596H2.43247C2.47142 1.94596 2.48217 1.94592 2.49095 1.9457C2.82477 1.93724 3.11928 1.72498 3.23286 1.41096C3.23585 1.40271 3.23928 1.39252 3.2516 1.35556L3.324 1.13835C3.36829 1.00517 3.4069 0.889117 3.4609 0.785405C3.67427 0.375662 4.06904 0.0911343 4.52523 0.0182849C4.6407 -0.000156045 4.763 -7.82777e-05 4.90337 1.25326e-05ZM4.05932 1.94596C4.09273 1.88043 4.12234 1.81234 4.14781 1.74192C4.15555 1.72054 4.16313 1.69778 4.17288 1.66854L4.23761 1.47432C4.29675 1.29691 4.31037 1.26073 4.32387 1.23479C4.395 1.09821 4.52659 1.00336 4.67865 0.979078C4.70754 0.974466 4.74614 0.972993 4.93314 0.972993H7.06686C7.25386 0.972993 7.29252 0.974466 7.32139 0.979078C7.47343 1.00336 7.60504 1.09821 7.6762 1.23479C7.68969 1.26073 7.70331 1.29691 7.7624 1.47432L7.82714 1.66842L7.85224 1.74194C7.87773 1.81235 7.90731 1.88043 7.94071 1.94596H4.05932Z" fill="black"/>
                                <path d="M2.05248 4.02055C2.03461 3.75246 1.80279 3.54963 1.53471 3.5675C1.26663 3.58537 1.06379 3.81718 1.08166 4.08527L1.38228 8.59446C1.43774 9.42655 1.48253 10.0986 1.58759 10.626C1.69682 11.1743 1.88261 11.6323 2.26634 11.9913C2.65008 12.3503 3.11941 12.5052 3.67377 12.5777C4.20697 12.6475 4.88051 12.6475 5.71441 12.6475H6.2845C7.1184 12.6475 7.79195 12.6475 8.32521 12.5777C8.87954 12.5052 9.3489 12.3503 9.73264 11.9913C10.1164 11.6323 10.3021 11.1743 10.4114 10.626C10.5165 10.0986 10.5612 9.42655 10.6167 8.59446L10.9173 4.08527C10.9352 3.81718 10.7323 3.58537 10.4642 3.5675C10.1962 3.54963 9.96433 3.75246 9.9465 4.02055L9.64812 8.49561C9.58987 9.36992 9.54836 9.97822 9.45716 10.4359C9.36868 10.8799 9.24524 11.1149 9.0679 11.2808C8.89057 11.4467 8.64784 11.5543 8.19898 11.613C7.73624 11.6735 7.12651 11.6745 6.25032 11.6745H5.74866C4.87247 11.6745 4.26271 11.6735 3.79997 11.613C3.3511 11.5543 3.10841 11.4467 2.93106 11.2808C2.75371 11.1149 2.63025 10.8799 2.54181 10.4359C2.45063 9.97822 2.4091 9.36992 2.35081 8.49561L2.05248 4.02055Z" fill="black"/>
                                <path d="M4.3302 5.18995C4.59755 5.16322 4.83598 5.35827 4.8627 5.62564L5.18702 8.86887C5.21375 9.13618 5.0187 9.37462 4.75133 9.40134C4.484 9.42807 4.2456 9.23302 4.21886 8.96565L3.89454 5.72242C3.86781 5.45512 4.06286 5.21667 4.3302 5.18995Z" fill="black"/>
                                <path d="M7.66979 5.18995C7.93716 5.21667 8.13221 5.45512 8.10549 5.72242L7.78116 8.96565C7.75444 9.23302 7.516 9.42807 7.24869 9.40134C6.98132 9.37462 6.78627 9.13618 6.813 8.86887L7.13732 5.62564C7.16404 5.35827 7.40248 5.16322 7.66979 5.18995Z" fill="black"/>
                            </svg>
                            Delete
                        </button>
                        </div>
                    <div style={{ overflowX: "auto" }}>
                        <table className="qc-table">
                            <thead>
                                <tr>
                                    {columns.map((col) => (
                                    <th key={col}>{col}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {pageRows.map((row: TableRow, pageRowIndex: number) => {
                                    const index = pageStartIndex + pageRowIndex;
                                    return (
                                    <tr
                                        key={index}
                                        style={{
                                        height: "40px",
                                        background: "#FFFFFF"
                                        }}
                                    >
                                        {columns.map((col) => (
                                        <td
                                            key={col}
                                            title={String(row[col as keyof TableRow])}
                                            style={{
                                            maxWidth: "180px",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            }}
                                        >
                                            {col === "variants" && Array.isArray(row[col])
                                              ? (row[col] as string[]).join(", ")
                                              : String(row[col as keyof TableRow])}
                                        </td>
                                        ))}
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    {tableData.length > 0 && (
                      <div className="cmw-param-card__pagination pad-x-15">
                        {totalPages > 1 && (
                          <Pagination>
                            <PaginationContent>
                              <PaginationItem>
                                <PaginationPrevious
                                  href="#"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    setPage((current) => Math.max(1, current - 1));
                                  }}
                                  aria-disabled={safePage === 1}
                                  tabIndex={safePage === 1 ? -1 : 0}
                                />
                              </PaginationItem>

                              {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNumber) => (
                                <PaginationItem key={pageNumber}>
                                  <PaginationLink
                                    href="#"
                                    isActive={pageNumber === safePage}
                                    onClick={(event) => {
                                      event.preventDefault();
                                      setPage(pageNumber);
                                    }}
                                  >
                                    {pageNumber}
                                  </PaginationLink>
                                </PaginationItem>
                              ))}

                              <PaginationItem>
                                <PaginationNext
                                  href="#"
                                  onClick={(event) => {
                                    event.preventDefault();
                                    setPage((current) => Math.min(totalPages, current + 1));
                                  }}
                                  aria-disabled={safePage === totalPages}
                                  tabIndex={safePage === totalPages ? -1 : 0}
                                />
                              </PaginationItem>
                            </PaginationContent>
                          </Pagination>
                        )}
                        <div className="cmw-param-card__pagination-size">
                          <label htmlFor="supplier-consolidation-rows-per-page">Rows per page</label>
                          <select
                            id="supplier-consolidation-rows-per-page"
                            value={rowsPerPage}
                            onChange={(event) => setRowsPerPage(Number(event.target.value))}
                          >
                            {[10, 20, 50, 100].map((count) => (
                              <option key={count} value={count}>
                                {count}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                </div>
            </div>
            <div className={`qc-right bg-white border border-color-d4 radius-10${rightCollapsed ? " is-collapsed" : ""}`}>
                <div className="border-bottom pad-12 qc-sidebar-head">
                    {!rightCollapsed && (
                      <div>
                        <div className="sidebar-title">Post Human Review</div>
                        <div className="sidebar-sub">Live Summary</div>
                      </div>
                    )}
                    <button
                      type="button"
                      className="qc-sidebar-toggle"
                      onClick={() => setRightCollapsed((value) => !value)}
                      aria-label={rightCollapsed ? "Expand live summary" : "Collapse live summary"}
                      title={rightCollapsed ? "Expand" : "Collapse"}
                    >
                      {rightCollapsed ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
                    </button>
                </div>
                {!rightCollapsed && (
                  <div className="sidebar-list pad-12">
                      <SidebarItem label="Total Unique Suppliers" count="12,481" />
                      <SidebarItem label="Total Normalized Suppliers" count="11,842" />
                      <SidebarItem label="Total Parent Mappings" count="3,218" />
                      <SidebarItem label="Total Spend Value" count="$2.84B" />
                      <SidebarItem label="Human Review Required" count="412" />
                      <div className="sidebar-disclaimer border border-color-d4 radius-10 flex gap-2 pad-10">
                          <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M4.46774 7.25012C4.4231 7.07709 4.33291 6.91918 4.20655 6.79282C4.08019 6.66646 3.92227 6.57626 3.74924 6.53162L0.681738 5.74062C0.629403 5.72577 0.583342 5.69425 0.550544 5.65085C0.517745 5.60744 0.5 5.55453 0.5 5.50012C0.5 5.44572 0.517745 5.39281 0.550544 5.3494C0.583342 5.306 0.629403 5.27448 0.681738 5.25962L3.74924 4.46812C3.92221 4.42353 4.08008 4.33341 4.20644 4.20714C4.33279 4.08088 4.42302 3.92307 4.46774 3.75012L5.25874 0.682625C5.27344 0.630084 5.30493 0.583795 5.3484 0.550822C5.39187 0.517848 5.44493 0.5 5.49949 0.5C5.55405 0.5 5.60711 0.517848 5.65058 0.550822C5.69405 0.583795 5.72553 0.630084 5.74024 0.682625L6.53074 3.75012C6.57538 3.92316 6.66557 4.08107 6.79193 4.20743C6.91829 4.33379 7.0762 4.42399 7.24924 4.46862L10.3167 5.25912C10.3695 5.27367 10.416 5.30513 10.4492 5.34866C10.4823 5.3922 10.5003 5.44541 10.5003 5.50012C10.5003 5.55484 10.4823 5.60805 10.4492 5.65159C10.416 5.69512 10.3695 5.72657 10.3167 5.74112L7.24924 6.53162C7.0762 6.57626 6.91829 6.66646 6.79193 6.79282C6.66557 6.91918 6.57538 7.07709 6.53074 7.25012L5.73974 10.3176C5.72503 10.3702 5.69355 10.4165 5.65008 10.4494C5.60661 10.4824 5.55355 10.5002 5.49899 10.5002C5.44443 10.5002 5.39137 10.4824 5.3479 10.4494C5.30443 10.4165 5.27294 10.3702 5.25824 10.3176L4.46774 7.25012Z" stroke="#492750" stroke-linecap="round" stroke-linejoin="round"/>
                              <path d="M9.49902 1V3" stroke="#492750" stroke-linecap="round" stroke-linejoin="round"/>
                              <path d="M10.499 2H8.49902" stroke="#492750" stroke-linecap="round" stroke-linejoin="round"/>
                              <path d="M1.49902 8V9" stroke="#492750" stroke-linecap="round" stroke-linejoin="round"/>
                              <path d="M1.99902 8.5H0.999023" stroke="#492750" stroke-linecap="round" stroke-linejoin="round"/>
                          </svg>
                          <span>
                              Graph enrichment adds D&B IDs, websites, and parent hierarchy to all normalized suppliers.
                          </span>
                      </div>
                  </div>
                )}
            </div>
      </div> 

      <div className="wizard-action-bar">
            <button className="action-bar__cancel btn btn--outline" onClick={onCancel}>Cancel</button>
            
            <button className="btn btn--outline m-left-auto" onClick={onBack}>
            <ChevronLeft size={14} /> Back
            </button>
            <button
              className="btn btn--primary"
              onClick={handleNext}
            >
              Spend Classification <ChevronRight size={14} />
            </button>
        </div>
    </div>
  );
}
